import { useEffect, useState } from 'react';

import { VideoRecorderIcon } from '@braskai/icons';

import { useClientStorageFlag } from '@/entities/clientStorage';
import { useScreenRecordAction } from '@/entities/recording';

import { useInterfaceLanguage } from '@/shared/i18n';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';

import { RASK_SCREEN_RECORDER_CHROME_STORE_URL, RECORDING_EXTENSION_PROMPT_STORAGE_KEY } from '../../lib/constants';
import { useCanShowRecordingExtensionPrompt } from '../../model/useCanShowRecordingExtensionPrompt';
import { useRecordingExtensionPromptStore } from '../../model/useRecordingExtensionPromptStore';
import { PuzzleIcon } from '../PuzzleIcon';
import {
  SAddressBar,
  SAvatar,
  SBrowserBar,
  SBrowserDots,
  SCard,
  SCloseButton,
  SCtaButton,
  SCursor,
  SDescription,
  SDialog,
  SExtensionName,
  SExtensionRow,
  SExtensionsHeader,
  SExtensionsMenu,
  SInstalledBadge,
  SPin,
  SPlaceholderIcon,
  SPlaceholderLine,
  SPreview,
  SPuzzleButton,
  SStoreCard,
  SStoreKind,
  SStoreLogo,
  SStoreName,
  SStorePill,
  SStoreText,
  STitle,
  SToolbar,
  SToolbarLogo,
} from './styled';

const TITLE_ID = 'recording-extension-prompt-title';

const RaskMark = ({ size }: { size: number }) => (
  <svg aria-hidden="true" fill="none" height={size} viewBox="0 0 22 22" width={size}>
    <path
      d="M3.23294 19.3715L3.23294 3.20287L20.21 3.271M3.23294 3.20287L18.6874 17.241"
      stroke="currentColor"
      strokeLinejoin="bevel"
      strokeMiterlimit="2.56141"
      strokeWidth="5.65901"
    />
  </svg>
);

const InstallPreview = () => {
  const { t } = useInterfaceLanguage();

  return (
    <SPreview $height={176} $marginBottom={24} aria-hidden="true">
      <SBrowserBar>
        <SBrowserDots>
          <span />
          <span />
          <span />
        </SBrowserDots>
        <SAddressBar />
        <SToolbarLogo>
          <RaskMark size={10} />
        </SToolbarLogo>
      </SBrowserBar>
      <SStoreCard>
        <SStoreLogo>
          <RaskMark size={20} />
        </SStoreLogo>
        <SStoreText>
          <SStoreName>{t('recordingModals.extension.recorderName')}</SStoreName>
          <SStoreKind>{t('recordingModals.extension.install.cardKind')}</SStoreKind>
        </SStoreText>
        <SStorePill>{t('recordingModals.extension.install.addToChrome')}</SStorePill>
      </SStoreCard>
    </SPreview>
  );
};

const PinPreview = () => {
  const { t } = useInterfaceLanguage();

  return (
    <SPreview $height={190} $marginBottom={16} aria-hidden="true">
      <SToolbar>
        <svg fill="none" height={16} viewBox="0 0 24 24" width={16}>
          <path
            d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 17l-5.2 2.6 1-5.8L3.5 9.7l5.9-.9L12 3.5z"
            stroke="currentColor"
            strokeLinejoin="round"
            strokeWidth="1.6"
          />
        </svg>
        <SPuzzleButton>
          <PuzzleIcon size={16} />
        </SPuzzleButton>
        <SAvatar>R</SAvatar>
        <svg fill="none" height={16} viewBox="0 0 24 24" width={16}>
          <circle cx="12" cy="5" fill="currentColor" r="1.5" />
          <circle cx="12" cy="12" fill="currentColor" r="1.5" />
          <circle cx="12" cy="19" fill="currentColor" r="1.5" />
        </svg>
      </SToolbar>
      <SExtensionsMenu>
        <SExtensionsHeader>{t('recordingModals.extension.pin.extensions')}</SExtensionsHeader>
        <SExtensionRow $active>
          <RaskMark size={18} />
          <SExtensionName>{t('recordingModals.extension.recorderName')}</SExtensionName>
          <SPin fill="none" height={16} viewBox="0 0 24 24" width={16}>
            <path
              d="M9 3.5h6l-1 4 2.5 3v1.5h-4V19l-1 1.5-1-1.5v-6H6.5V11l2.5-3-1-4z"
              fill="currentColor"
              stroke="currentColor"
              strokeLinejoin="round"
              strokeWidth="1.2"
            />
          </SPin>
        </SExtensionRow>
        <SExtensionRow>
          <SPlaceholderIcon />
          <SPlaceholderLine />
        </SExtensionRow>
      </SExtensionsMenu>
      <SCursor fill="none" height={20} viewBox="0 0 24 24" width={20}>
        <path d="M5 3l14 8-6 1.5L11 19 5 3z" fill="currentColor" stroke="#fff" strokeLinejoin="round" strokeWidth="1.2" />
      </SCursor>
      <SInstalledBadge>
        <svg fill="none" height={12} viewBox="0 0 24 24" width={12}>
          <path
            d="M5 12.5l4.5 4.5L19 7"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2.6"
          />
        </svg>
        {t('recordingModals.extension.pin.installed')}
      </SInstalledBadge>
    </SPreview>
  );
};

/**
 * Walks the user through installing and pinning the Rask Screen Recorder Chrome extension, which adds auto-zooms
 * while recording. Offered automatically ('new_recording') at most once per user, before the recorder opens, and
 * leaving it then continues to the recorder. Opened from the Records page banner ('records_banner') it shows on every
 * click and leaves the once-per-user flag alone.
 */
export const RecordingExtensionPromptModal = () => {
  const { t } = useInterfaceLanguage();
  const isRequested = useRecordingExtensionPromptStore((state) => state.isOpen);
  const step = useRecordingExtensionPromptStore((state) => state.step);
  const continueToRecorder = useRecordingExtensionPromptStore((state) => state.continueToRecorder);
  const source = useRecordingExtensionPromptStore((state) => state.source);
  const closePrompt = useRecordingExtensionPromptStore((state) => state.close);
  const openPrompt = useRecordingExtensionPromptStore((state) => state.open);
  const { isPlatformEligible, isViewportWide } = useCanShowRecordingExtensionPrompt();
  const openScreenRecord = useScreenRecordAction();
  const [isShowing, setIsShowing] = useState(false);
  // The user asked for the prompt themselves, so the once-per-user limit of the automatic offer does not apply.
  const isUserRequested = source === 'records_banner';
  const flag = useClientStorageFlag(RECORDING_EXTENSION_PROMPT_STORAGE_KEY, {
    enabled: isRequested && !isUserRequested && isPlatformEligible && isViewportWide,
  });
  const { hasShown, isError, isLoading, markAsShown } = flag;

  useEffect(() => {
    if (!isRequested || isShowing) return;

    // A dropped request never holds up the recording it stood in for.
    const dropRequest = () => {
      closePrompt();
      if (continueToRecorder) void openScreenRecord();
    };

    // A platform that can never install the extension drops the request without spending the once-per-user flag.
    if (!isPlatformEligible) {
      dropRequest();
      return;
    }

    // An eligible browser in a narrow window keeps the request until the window widens this session, unless a
    // recording is waiting for it.
    if (!isViewportWide) {
      if (continueToRecorder) dropRequest();
      return;
    }

    if (!isUserRequested) {
      if (isLoading) return;

      // Without a reliable "already shown" answer, skip rather than risk showing the prompt twice.
      if (isError || hasShown) {
        dropRequest();
        return;
      }

      markAsShown();
    }

    setIsShowing(true);
    MixpanelEvents.extensionInstallPromptViewed({ source });
  }, [
    closePrompt,
    continueToRecorder,
    hasShown,
    isError,
    isLoading,
    isPlatformEligible,
    isRequested,
    isShowing,
    isUserRequested,
    isViewportWide,
    markAsShown,
    openScreenRecord,
    source,
  ]);

  if (!isRequested || !isShowing) return null;

  const handleClose = () => {
    MixpanelEvents.extensionInstallPromptDismiss({ source, step });
    setIsShowing(false);
    closePrompt();
    if (continueToRecorder) void openScreenRecord();
  };

  const handleInstall = () => {
    MixpanelEvents.extensionInstallClick({ source });
    window.open(RASK_SCREEN_RECORDER_CHROME_STORE_URL, '_blank', 'noopener,noreferrer');
    openPrompt({ continueToRecorder, source, step: 'pin' });
  };

  const handleRecord = () => {
    setIsShowing(false);
    closePrompt();
    void openScreenRecord();
  };

  const isInstallStep = step === 'install';

  return (
    <SDialog aria-labelledby={TITLE_ID} data-testid="recording-extension-prompt" open onClose={handleClose}>
      <SCard>
        <SCloseButton
          aria-label={t('recordingModals.extension.closeAria')}
          data-testid="recording-extension-prompt-close"
          type="button"
          onClick={handleClose}
        >
          <svg fill="none" height={18} viewBox="0 0 24 24" width={18}>
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
          </svg>
        </SCloseButton>

        <STitle id={TITLE_ID}>
          {isInstallStep ? t('recordingModals.extension.install.title') : t('recordingModals.extension.pin.title')}
        </STitle>

        {isInstallStep ? <InstallPreview /> : <PinPreview />}

        <SDescription>
          {isInstallStep
            ? t('recordingModals.extension.install.description')
            : t('recordingModals.extension.pin.description')}
        </SDescription>

        {isInstallStep ? (
          <SCtaButton data-testid="recording-extension-prompt-install" variant="primary" fullWidth onClick={handleInstall}>
            {t('recordingModals.extension.install.cta')}
          </SCtaButton>
        ) : (
          <SCtaButton data-testid="recording-extension-prompt-record" variant="primary" fullWidth onClick={handleRecord}>
            <VideoRecorderIcon height={18} width={18} />
            {t('recordingModals.extension.pin.cta')}
          </SCtaButton>
        )}
      </SCard>
    </SDialog>
  );
};
